import { z } from 'zod';

// Slug allows: Thai (U+0E00-U+0E7F) + lowercase Latin + digits, separated by single dashes.
// Title-case Latin is intentionally rejected so authors must lowercase manually.
const slugRx = /^[฀-๿a-z0-9]+(?:-[฀-๿a-z0-9]+)*$/u;

const postStatusEnum = z.enum(['draft', 'pending_review', 'scheduled', 'published', 'archived']);

export const createPostSchema = z
  .object({
    title: z.string().min(1, 'กรุณาใส่หัวข้อ').max(255),
    slug: z
      .string()
      .regex(slugRx, 'slug ใช้ได้: ไทย / a-z / 0-9 และ - เท่านั้น')
      .max(255)
      .optional(),
    summary: z.string().max(500).optional().nullable(),
    body: z.string().default(''),
    language: z.enum(['th', 'en']).default('th'),
    visibility: z.enum(['public', 'members', 'password']).default('public'),
    isSticky: z.boolean().default(false),
    isBreaking: z.boolean().default(false),
    isFeatured: z.boolean().default(false),
    /** ข่าวประชาสัมพันธ์ — โพสต์ที่มี shelf-life. ติ๊ก = บังคับ expiryAt;
     *  cron 5 นาที flip → status='archived' เมื่อถึงกำหนด. */
    isPressRelease: z.boolean().default(false),
    /** ISO 8601 datetime. nullable เพราะ schema partial ต้อง explicit-null
     *  ได้ตอน update (เคลียร์ field). Validation ของ "ต้องกรอกถ้า PR=true"
     *  ผูกอยู่ใน refine() ด้านล่าง. */
    expiryAt: z.string().datetime().optional().nullable(),
    allowComments: z.boolean().default(true),
    metaTitle: z.string().max(255).optional().nullable(),
    metaDescription: z.string().max(500).optional().nullable(),
    focusKeyword: z.string().max(100).optional().nullable(),
    featuredImageId: z.string().uuid().optional().nullable(),
    categoryIds: z.array(z.string().uuid()).optional(),
    tagIds: z.array(z.string().uuid()).optional(),
  })
  .refine((v) => !v.isPressRelease || (v.expiryAt != null && v.expiryAt.length > 0), {
    message: 'กรุณากำหนดวันหมดอายุของข่าวประชาสัมพันธ์',
    path: ['expiryAt'],
  });

export type CreatePostInput = z.infer<typeof createPostSchema>;

// `.partial()` ไม่ทำงานบน ZodEffects (refine wrapper). ดึง shape ออกแล้ว
// partial → re-wrap refine ใหม่: PATCH ที่ส่งแค่บาง field ก็ผ่าน
// แต่ถ้าส่ง isPressRelease=true มา ต้องส่ง expiryAt ที่ไม่ใช่ null ด้วย.
const createPostShape = createPostSchema._def.schema.shape;
export const updatePostSchema = z
  .object(createPostShape)
  .partial()
  .refine(
    (v) => {
      // ถ้าไม่ได้แตะ isPressRelease ใน patch นี้ → ผ่าน (validate ของรอบที่
      // ตั้ง flag จริงดูแลให้แล้ว)
      if (v.isPressRelease === undefined) return true;
      // ถ้า set isPressRelease=true → expiryAt ต้องถูก set ในรอบเดียวกัน
      // (FE ส่งทั้งคู่ทุกครั้งที่ติ๊ก checkbox อยู่แล้ว)
      if (v.isPressRelease === true) {
        return typeof v.expiryAt === 'string' && v.expiryAt.length > 0;
      }
      return true;
    },
    { message: 'กรุณากำหนดวันหมดอายุของข่าวประชาสัมพันธ์', path: ['expiryAt'] },
  );

export type UpdatePostInput = z.infer<typeof updatePostSchema>;

export const listPostsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().positive().max(100).default(20),
  // Accept "draft" or "draft,scheduled" — coerce single string → array via preprocess.
  // URL serialization is always comma-joined; empty/undefined → no filter.
  status: z
    .preprocess((v) => {
      if (typeof v === 'string') {
        const parts = v
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        return parts.length > 0 ? parts : undefined;
      }
      return v;
    }, z.array(postStatusEnum).min(1).optional())
    .optional(),
  q: z.string().trim().min(1).max(255).optional(),
  authorId: z.string().uuid().optional(),
  /** Filter to posts that belong to this category. Combines with status
   *  + authorId via AND. Single id only — no multi-cat selection in
   *  admin yet; that would need a SELECT DISTINCT to dedupe rows. */
  categoryId: z.string().uuid().optional(),
  /** Symmetric to categoryId — single tag id, AND-combined with other
   *  filters. Most common driver is "see all posts tagged Breaking". */
  tagId: z.string().uuid().optional(),
  sortBy: z.enum(['updatedAt', 'createdAt', 'title', 'publishedAt']).default('updatedAt'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
  /** When true, returns ONLY soft-deleted posts (the "trash" view) and
   *  ignores the `status` filter. When false/absent (default), excludes
   *  soft-deleted posts as before. */
  trash: z.coerce.boolean().optional().default(false),
  /** Editorial-flag filters — match posts where the flag is true.
   *  Multiple are AND-combined (e.g. sticky+breaking shows only posts
   *  with BOTH flags). Pass false/absent to skip. */
  sticky: z.coerce.boolean().optional(),
  breaking: z.coerce.boolean().optional(),
  featured: z.coerce.boolean().optional(),
});

export type ListPostsQuery = z.infer<typeof listPostsQuerySchema>;

export const publishPostSchema = z.object({
  scheduledAt: z.string().datetime().optional(),
});

export type PublishPostInput = z.infer<typeof publishPostSchema>;

/**
 * Manually adjust a post's all-time `view_count` (super_admin only).
 * - `mode: 'set'` → view_count = value (absolute)
 * - `mode: 'add'` → view_count += value (bump)
 * `value` is a non-negative integer; the endpoint caps the resulting
 * total at 100,000,000. This never touches `post_view_daily`, so
 * trending / "popular" windows stay untouched.
 */
export const setViewCountSchema = z.object({
  mode: z.enum(['set', 'add']),
  value: z.number().int('ต้องเป็นจำนวนเต็ม').min(0, 'ต้องไม่ติดลบ').max(100_000_000),
});

export type SetViewCountInput = z.infer<typeof setViewCountSchema>;

/**
 * Bulk add/remove tags across multiple posts in one request.
 * Either `addTagIds` or `removeTagIds` must be non-empty (or both).
 * Cap at 100 posts/100 tags per request to keep the worker
 * comfortably within the CPU budget.
 */
export const bulkTagPostsSchema = z
  .object({
    ids: z.array(z.string().min(1).max(64)).min(1).max(100),
    addTagIds: z.array(z.string().uuid()).max(100).optional(),
    removeTagIds: z.array(z.string().uuid()).max(100).optional(),
  })
  .refine(
    (v) => (v.addTagIds?.length ?? 0) + (v.removeTagIds?.length ?? 0) > 0,
    'Provide at least one tag to add or remove',
  );

export type BulkTagPostsInput = z.infer<typeof bulkTagPostsSchema>;

/**
 * Same shape as bulkTagPostsSchema but for the category junction.
 * Two separate endpoints rather than a unified one because the
 * downstream tables differ (post_tags vs post_categories) and the
 * audit trail is clearer when the action names differ too.
 */
export const bulkCategoryPostsSchema = z
  .object({
    ids: z.array(z.string().min(1).max(64)).min(1).max(100),
    addCategoryIds: z.array(z.string().uuid()).max(100).optional(),
    removeCategoryIds: z.array(z.string().uuid()).max(100).optional(),
  })
  .refine(
    (v) => (v.addCategoryIds?.length ?? 0) + (v.removeCategoryIds?.length ?? 0) > 0,
    'Provide at least one category to add or remove',
  );

export type BulkCategoryPostsInput = z.infer<typeof bulkCategoryPostsSchema>;

/**
 * Public list query schema — narrow subset of admin schema, no status (always
 * filtered to published server-side), no sortBy/sortDir flexibility (always
 * publishedAt desc with sticky first).
 */
export const publicListPostsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().positive().max(50).default(20),
  category: z.string().min(1).max(100).optional(),
  tag: z.string().min(1).max(100).optional(),
  language: z.enum(['th', 'en']).optional(),
  q: z.string().trim().min(1).max(255).optional(),
});

export type PublicListPostsQuery = z.infer<typeof publicListPostsQuerySchema>;
